// Read-only cover elements (stickers / icons / photos the team placed on the
// cover, lib/coverElements.ts) for the screens that SHOW a cover without
// editing it — the customer flow's cover spread (CoverSpreadPreview).
//
// Same CSS as the editor's PageElementOverlay for these types: a box at
// left/top/width/height % of the face frame, `rotate() scaleX() scaleY()` about
// its centre; an icon contain-fits (or fills) the box, a photo cover-fills it
// (its crop window through cropCss, the PDF's cropSource twin). The box must
// sit in a face of the face's printed aspect — the caller's job.
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { elementsOnFace, faceAspect, readCoverElements, type CoverElement, type CoverFace } from '@/lib/coverElements';
import { iconDisplayUrl } from '@/lib/coverIcons';
import { cropCss } from '@/lib/decor/cropMath';
import { shadowPlan } from '@/lib/decor/decorStyle';
import { ElementShadow } from '@/components/page/PageDecor';
import { SafeSvg } from '@/components/SafeSvg';
import { CoverTextContent } from '@/components/CoverTextBox';

function PhotoBox({ el, src, boxAR }: { el: CoverElement; src: string; boxAR: number }) {
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const crop = el.crop && nat ? cropCss(el.crop, nat, boxAR) : null;
  return (
    <div className="absolute inset-0 overflow-hidden">
      <img
        src={src}
        alt=""
        draggable={false}
        loading="lazy"
        decoding="async"
        onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        className={crop ? 'absolute' : 'w-full h-full object-cover'}
        style={crop ? { left: `${crop.left}%`, top: `${crop.top}%`, width: `${crop.width}%`, height: `${crop.height}%`, maxWidth: 'none' } : undefined}
      />
    </div>
  );
}

export function CoverElementsView({ elements, face, photoUrl, style }: {
  elements: unknown;
  face: CoverFace;
  photoUrl?: (photoId: string) => string | null;
  style?: CSSProperties;
}) {
  const els = elementsOnFace(readCoverElements(elements), face);
  if (!els.length) return null;
  const plan = shadowPlan(els);
  return (
    // An inline-size query container: a cover TEXT sizes its letters in `cqi`
    // of the face, like the editor's layer and the print (storiz-render-parity).
    <div data-cover-el-view={face} className="absolute pointer-events-none" style={{ ...style, zIndex: 15, isolation: 'isolate', containerType: 'inline-size' }}>
      {els.map((el, ei) => {
        const t = [el.rotation ? `rotate(${el.rotation}deg)` : '', el.flipH || el.flipV ? `scaleX(${el.flipH ? -1 : 1}) scaleY(${el.flipV ? -1 : 1})` : '']
          .filter(Boolean).join(' ') || undefined;
        const photo = el.type === 'photo' && el.photoId ? photoUrl?.(el.photoId) ?? null : null;
        return (
          <div key={el.id} style={{ display: 'contents' }}>
            {(plan.get(ei) ?? []).map((sh) => (
              <ElementShadow key={`sh-${sh.id}`} el={sh} imageSrc={sh.type === 'icon' && sh.imageUrl ? iconDisplayUrl(sh) ?? null : null} />
            ))}
            <div
              data-cover-el={el.id}
              style={{
                position: 'absolute', left: `${el.x}%`, top: `${el.y}%`, width: `${el.width}%`, height: `${el.height}%`,
                transform: t, transformOrigin: 'center', opacity: el.type === 'text' ? 1 : el.opacity ?? 1,
              }}
            >
              {/* A text's own opacity is on its words (CoverTextContent), the band keeps its own. */}
              {el.type === 'text' && <CoverTextContent el={el} />}
              {el.type === 'icon' && el.imageUrl && !el.svg && (
                <img src={iconDisplayUrl(el)} alt="" draggable={false} loading="lazy" decoding="async"
                  className={`w-full h-full ${el.fit === 'fill' ? 'object-fill' : 'object-contain'}`} />
              )}
              {el.type === 'icon' && el.svg && (
                <SafeSvg as="div" svg={el.svg}
                  className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
                  style={{ color: el.iconColor ?? el.fontColor ?? '#1F2937' }} />
              )}
              {photo && <PhotoBox el={el} src={photo} boxAR={(el.width / el.height) * faceAspect(face)} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}
